import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { FaAngleDown, FaAngleUp } from "react-icons/fa";
import { twMerge } from "tailwind-merge";
import { cardColors } from "../../constants/colors";
import { useSettings } from "../../context/SettingsContext";
import { GridItem, StringIconGridItem } from "../../types/GridItem";

type AccessibilityAdjustableCardProps = {
  item: GridItem;
  gridItems: StringIconGridItem[];
  selectedItems: string[];
  currentSlide: Record<string, number>;
  slidesData: Record<string, { label: string }[]>;
  setButtonRef: (id: string, el: HTMLButtonElement | null) => void;
  handleKeyDown?: (e: React.KeyboardEvent) => void;
  handleSlideChange: (title: string, direction: "up" | "down") => void;
  tooltip: JSX.Element;
};

const getGridItem = (key: string, gridItems: StringIconGridItem[]) =>
  gridItems.find((item) => item.settingKey === key);

const AccessibilityAdjustableCard: React.FC<AccessibilityAdjustableCardProps> =
  React.memo(
    ({
      item,
      gridItems,
      selectedItems,
      currentSlide,
      slidesData,
      setButtonRef,
      handleKeyDown,
      handleSlideChange,
      tooltip: TooltipComponent,
    }) => {
      const { t } = useTranslation();
      const { oversizeWidget, widgetButtonColor, widgetButtonTextColor } =
        useSettings();

      const isSelected = selectedItems.includes(
        getGridItem(item.settingKey, gridItems)!.title
      );

      return (
        <button
          tabIndex={0}
          ref={(el) => setButtonRef(item.title, el)}
          style={{ height: oversizeWidget ? "28vh" : "" }}
          className={twMerge(
            clsx(
              "w-full sm:w-[60%] accessibility-card h-28 cursor-pointer abosluet flex flex-col items-center justify-center rounded-[20.52px] py-6 bg-blue-[#F2F7FA] md:hover:bg-blue-200 hover:shadow-lg",
              isSelected
                ? clsx(
                    cardColors.selected.background,
                    "border-2",
                    cardColors.selected.border
                  )
                : clsx(cardColors.default.background, "text-gray-700")
            )
          )}
          onKeyDown={handleKeyDown}
        >
          {/* TOOLTIP COMPONENT */}
          {TooltipComponent}

          <div
            className={clsx(
              "flex",
              oversizeWidget
                ? "flex-col justify-center items-center"
                : "justify-evenly"
            )}
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center mb-2"
              style={{
                height: oversizeWidget ? "55px" : "",
                width: oversizeWidget ? "55px" : "",
                marginTop: oversizeWidget ? "0px" : "",
                marginLeft: oversizeWidget ? "auto" : "",
                marginRight: oversizeWidget ? "auto" : "",
              }}
            >
              {item.icon}
            </div>
            <p
              className={clsx(
                "text-sm text-gray-700 pl-2 text-center break-words",
                oversizeWidget ? "pt-6 pb-6" : "pt-0 pb-0"
              )}
              style={{
                fontSize: oversizeWidget ? "1.375rem" : "",
                color: isSelected ? "white" : oversizeWidget ? "black" : "",
                fontWeight: oversizeWidget ? "600" : "normal",
                wordBreak: "break-word",
                paddingTop: oversizeWidget ? "0px" : "10px",
                lineHeight: "1.5",
                maxWidth: "200px",
              }}
            >
              {t(item.label)}
            </p>
          </div>
          <div className="flex items-center space-x-2 my-1 rounded-full bg-white w-[70%] justify-between">
            <button
              tabIndex={0}
              aria-label="Previous slide"
              className="flex items-center justify-center h-8 w-8 rounded-full p-1 hover:opacity-90 transition-opacity"
              style={{
                //backgroundColor: widgetButtonColor,
                color: widgetButtonTextColor,
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleSlideChange(item.title, "up");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSlideChange(item.title, "up");
                }
              }}
            >
              <FaAngleDown className="h-8 w-8 p-1" />
            </button>
            <p
              className={clsx(
                "text-xs py-2 whitespace-nowrap",
                oversizeWidget ? "!font-semibold" : undefined
              )}
              style={{ fontWeight: oversizeWidget ? 600 : 400 }}
            >
              {t(
                slidesData[getGridItem(item.settingKey, gridItems)!.title]?.[
                  currentSlide[
                    getGridItem(item.settingKey, gridItems)!.title
                  ] || 0
                ]?.label || "default"
              )}
            </p>

            {/* Up Icon */}
            <button
              tabIndex={0}
              aria-label="Next slide"
              className="flex items-center justify-center h-8 w-8 rounded-full p-1 hover:opacity-90 transition-opacity"
              style={{
                //backgroundColor: widgetButtonColor,
                color: widgetButtonTextColor,
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleSlideChange(item.title, "down");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSlideChange(item.title, "down");
                }
              }}
            >
              <FaAngleUp className="h-8 w-8 p-1" />
            </button>
          </div>
        </button>
      );
    }
  );

AccessibilityAdjustableCard.displayName = "AccessibilityAdjustableCard";

export default AccessibilityAdjustableCard;
